import React, { useMemo } from 'react';
import { Button } from '@heroui/react';
import { AccountMultiSelect, CustomCheckbox, Typography } from '../../../../../components';
import { useTranslations } from '../../../../../contexts/translation.context';

interface Account {
  id: string;
  of_user_id: string;
  of_user_name?: string;
  username?: string;
  avatar?: string;
}

interface AccountSelectorProps {
  accounts: Account[];
  selectedAccounts: string[];
  onSelectionChange: (selectedValues: string[]) => void;
  isLoading: boolean;
  onAddAccount: () => void;
  useAccountNameAsOffer: boolean;
  onUseAccountNameChange: (checked: boolean) => void;
  error?: string;
  touched?: boolean;
  onClose?: () => void;
}

const AccountSelector: React.FC<AccountSelectorProps> = ({
  accounts,
  selectedAccounts,
  onSelectionChange,
  isLoading,
  onAddAccount,
  useAccountNameAsOffer,
  onUseAccountNameChange,
  error,
  touched,
  onClose,
}) => {
  const { t } = useTranslations();
  const placeholder = t('salesFans.selectAccounts');
  const isError = Boolean(error && touched);

  const triggerClassName = useMemo(() => {
    const padding = selectedAccounts.length > 0 ? 'px-1' : 'px-3';
    const border = isError ? 'border-auth-strength-weak' : 'border-auth-strength-none';
    return `bg-white border border-[0.125rem] rounded-2 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] ${border} ${padding}`;
  }, [isError, selectedAccounts.length]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-2">
        <Typography
          variant="body2"
          className={isError ? 'text-auth-strength-weak' : 'text-auth-ink'}
        >
          {t('salesFans.verifiedAccount')}
          <span className={isError ? 'text-auth-strength-weak' : 'text-partner'}>*</span>
        </Typography>
      </div>

      {accounts.length > 0 ? (
        <AccountMultiSelect<Account>
          accounts={accounts}
          selectedIds={selectedAccounts}
          onSelectionChange={onSelectionChange}
          getId={(account) => account.of_user_id}
          getAvatar={(account) => account.avatar}
          getLabel={(account) =>
            `@${account.of_user_name || account.username || account.of_user_id}`
          }
          getDescription={() => t('salesFans.free')}
          placeholder={placeholder}
          isLoading={isLoading}
          isRequired
          onClose={onClose}
          className="border-0"
          selectClassNames={{
            trigger: triggerClassName,
            value: 'text-sm text-zinc-500',
            popoverContent: '!rounded-2',
            listbox: 'p-2',
            label: '!mb-2',
          }}
        />
      ) : (
        <div className="space-y-2" />
      )}
      <div className="flex flex-col h-full items-start mt-3">
        <CustomCheckbox
          isSelected={useAccountNameAsOffer}
          size="sm"
          onValueChange={onUseAccountNameChange}
          className="text-sm min-h-5 text-black"
        >
          {t('createOffer.useAccountNameAsOffer')}
        </CustomCheckbox>
        {isError && (
          <Typography variant="body2" className="text-auth-strength-weak mt-3 text-xs">
            {error}
          </Typography>
        )}
        <Button
          size="sm"
          variant="flat"
          color="primary"
          className="h-8 mt-3 px-3 text-xs bg-[#e6f1fe] text-partner rounded-lg"
          onPress={onAddAccount}
        >
          {t('createOffer.addAccount')}
        </Button>
      </div>
    </div>
  );
};

export default AccountSelector;
